import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  cxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import {
  CreateFAQPayload,
  CreateTipPayload,
  UpdateFAQPayload,
  UpdateFAQStatusPayload,
  UpdateTipPayload,
  UpdateTipStatusPayload,
  CreateServiceCenterPayload,
  UpdateServiceCenterStatusPayload,
  UpdateServiceCenterPayload,
} from "./types";

export const selfServiceApi = {
  getFaqList: async (params: {
    remote_utility_id: string;
    is_pagination_required: any;
    page: number;
    limit: number;
    show_inactive?: any;
    search_data?: string;
    faq_category?: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/faq",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push(
          "is_pagination_required",
          params.is_pagination_required.toString(),
        );
        qs.push("page", params.page.toString());
        qs.push("limit", params.limit.toString());
        if (params.show_inactive !== undefined) {
          qs.push("show_inactive", params.show_inactive.toString());
        }
        if (params.search_data) {
          qs.push("search_field", "faq");
          qs.push("search_data", params.search_data);
        }
        if (params.faq_category) {
          qs.push("faq_category", params.faq_category);
        }
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getFaqCategory: async (params: {
    remote_utility_id: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "choices/faq_category",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getTipsData: async (params: {
    remote_utility_id: string;
    is_pagination_required: any;
    page: number;
    limit: any;
    show_inactive: any;
    search_data?: string;
    utility_service?: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/tips",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("is_pagination_required", params.is_pagination_required);
        qs.push("page", params.page);
        qs.push("limit", params.limit);
        qs.push("show_inactive", params.show_inactive);
        if (params.search_data) {
          qs.push("search_data", params.search_data);
        }
        if (params.utility_service) {
          qs.push("utility_service", params.utility_service);
        }
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getDashboardDetails: async (params: {
    remote_utility_id: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "self-service-summary",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
  // Service Center APIs
  getServiceCenterList: async (params: {
    remote_utility_id: string;
    show_inactive?: boolean;
    page: number;
    limit: number;
    search_data?: string;
    type?: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "service-center",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        if (params.show_inactive !== undefined) {
          qs.push("show_inactive", params.show_inactive.toString());
        }
        qs.push("page", params.page.toString());
        qs.push("limit", params.limit.toString());
        if (params.search_data) {
          qs.push("search_data", params.search_data);
        }
        if (params.type) {
          qs.push("type", params.type);
        }
      },
    );
    const response = await cxApiClient.get(url);
    return response.data;
  },
  searchTips: async (params: {
    remote_utility_id: string;
    search_data: string;
    search_field?: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "search/onboarding_config",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("search_field", params.search_field || "tips");
        qs.push("search_data", params.search_data);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  searchFAQ: async (params: {
    remote_utility_id: string | number;
    search_data: string;
    search_field?: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "search/onboarding_config",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id.toString());
        qs.push("search_field", params.search_field || "faq");
        qs.push("search_data", params.search_data);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
  createServiceCenter: async (
    payload: CreateServiceCenterPayload,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrl("cx", "service-center");
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  updateServiceCenterStatus: async (
    id: string | number,
    payload: UpdateServiceCenterStatusPayload,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrl("cx", `service-center/${id}`);
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  updateServiceCenter: async (
    id: string,
    payload: UpdateServiceCenterPayload,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrl("cx", `service-center/${id}`);
    const response = await cxApiClient.put(url, payload);
    return response.data;
  },

  addTips: async (payload: CreateTipPayload): Promise<any> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/tips");
    const response = await onboardingApiClient.post(url, payload);
    return response.data;
  },

  updateTip: async (payload: UpdateTipPayload): Promise<any> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/tips");
    const response = await onboardingApiClient.patch(url, payload);
    return response.data;
  },

  addFaqs: async (payload: CreateFAQPayload): Promise<any> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/faq");
    const response = await onboardingApiClient.post(url, payload);
    return response.data;
  },

  updateFaqs: async (payload: UpdateFAQPayload): Promise<any> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/faq");
    const response = await onboardingApiClient.patch(url, payload);
    return response.data;
  },

  updateFaqStatus: async (payload: UpdateFAQStatusPayload): Promise<any> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/faq");
    const response = await onboardingApiClient.patch(url, payload);
    return response.data;
  },

  updateTipStatus: async (payload: UpdateTipStatusPayload): Promise<any> => {
    const url = ApiEndpoints.createUrl("onboarding", "config-map/tips");
    const response = await onboardingApiClient.patch(url, payload);
    return response.data;
  },
  getCity: async (params: { remote_utility_id: string }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/territory_json",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("config_level", "county");
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getArea: async (params: {
    remote_utility_id: string;
    code_list: string | string[];
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/territory_json",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("config_level", "area");
        const codes = Array.isArray(params.code_list) ? params.code_list : [params.code_list];
        for (const code of codes) {
          qs.push("code_list", code);
        }
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getSubArea: async (params: {
    remote_utility_id: string;
    code_list: string | string[];
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/territory_json",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("config_level", "sub_area");
        const codes = Array.isArray(params.code_list) ? params.code_list : [params.code_list];
        for (const code of codes) {
          qs.push("code_list", code);
        }
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
  getPremise: async (params: {
    remote_utility_id: string;
    code_list: string | string[];
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/territory_json",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("config_level", "premise");
        const codes = Array.isArray(params.code_list) ? params.code_list : [params.code_list];
        for (const code of codes) {
          qs.push("code_list", code);
        }
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
  getServiceCenters: async (params: {
    remote_utility_id: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "choices/service_center_type",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  //https://api-cx-staging.bynry.com/api/cx/service-center/13/
  deleteServiceCenter: async (params: { id: any }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      `service-center/${params.id}`,
    );
    const response = await cxApiClient.delete(url);
    return response.data;
  },

      deleteFaq: async (params: {
      remote_utility_id: string;
      code: string;
      type:string
    }): Promise<any> => {
      const url = ApiEndpoints.createUrlWithQueryParameters(
        "onboarding",
        `config-map/${params.type}`,
        (qs) => {
          qs.push("remote_utility_id", params.remote_utility_id);
          qs.push("code", params.code);
        }
      );
      const response = await onboardingApiClient.delete(url);
      return response.data;
    },
    addSelfServieBranding: async (payload: any): Promise<any> => {
      const url = ApiEndpoints.createUrlWithQueryParameters(
        "cx",     
        "self-service/branding",
      );
      const response = await cxApiClient.post(url, payload);
      return response.data;
    },
    getSelfServieBranding: async (remoteUtilityId: string): Promise<any> => {
      const url = ApiEndpoints.createUrlWithQueryParameters(
        "cx",
        "self-service/branding",
        (qs) => {
          qs.push("remote_utility_id", remoteUtilityId);
        }
      );
      const response = await cxApiClient.get(url);
      return response.data;
    },
    updateSelfServieBranding: async (id: string | number, payload: any): Promise<any> => {
      const url = ApiEndpoints.createUrlWithQueryParameters(
        "cx",
        `self-service/branding/${id}`,
      );
      const response = await cxApiClient.put(url, payload);
      return response.data;
    },
    getWidgets: async (params: { remote_utility_id: any }): Promise<any> => {
      const url = ApiEndpoints.createUrlWithQueryParameters(
        "cx",
        "self-service/widgets",
        (qs) => {
          qs.push("remote_utility_id", params.remote_utility_id);
        }
      );
      const response = await cxApiClient.get(url);
      return response.data;
    },
    updateWidgets: async (payload: any): Promise<any> => {
      const url = ApiEndpoints.createUrlWithQueryParameters(
        "cx",
        "self-service/widgets",
      );
      const response = await cxApiClient.put(url, payload);
      return response.data;
    },
    getGuestPortalBranding: async (params: { remote_utility_id: any }): Promise<any> => {
      const url = ApiEndpoints.createUrlWithQueryParameters(
        "cx",
        "guest-portal-branding",
        (qs) => {
          qs.push("remote_utility_id", params.remote_utility_id);
        }
      );
      const response = await cxApiClient.get(url);
      return response.data;
    },
    updateGuestPortalBranding: async (payload: FormData): Promise<any> => {
      const url = ApiEndpoints.createUrlWithQueryParameters(
        "cx",
        "guest-portal-branding",
      );
      const response = await cxApiClient.put(url, payload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    },
  };
